import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'

const colors = {
  teal: '#145F6C',
  tealDark: '#0D3135',
  copper: '#AB9381',
  cream: '#F7F8F6',
  nearBlack: '#0A0B0B',
}

const ease = [0.16, 1, 0.3, 1] as const

function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

const STEPS = [
  {
    num: '01',
    title: 'Share Your Link',
    body: 'Send your unique referral link to a friend, neighbor, or anyone who could use a little less on their plate.',
  },
  {
    num: '02',
    title: 'They Join Hudson',
    body: 'When your referral becomes a Hudson member, we\u2019ll confirm your credit automatically.',
  },
  {
    num: '03',
    title: 'You Both Win',
    body: 'You each receive one month of membership free. Simple as that.',
  },
] as const

const ReferPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.cream }}>
      <MetaTags
        title="Refer a Friend | Hudson My Home"
        description="Give someone you love a Hudson. Refer a neighbor and you both get a free month of membership."
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section
          className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden text-center"
          style={{ backgroundColor: colors.cream }}
        >
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${colors.teal} 0%, transparent 55%)`,
            }}
          />
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 relative">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-sans text-sm font-semibold uppercase tracking-[0.25em] mb-5"
              style={{ color: colors.copper }}
            >
              Refer a Friend
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12, ease }}
              className="font-serif font-normal text-grey-950 max-w-3xl mx-auto"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 3.25rem)', lineHeight: 1.08, letterSpacing: '-0.02em' }}
            >
              Give Someone You Love a Hudson.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.18 }}
              className="mt-8 font-sans text-base sm:text-lg leading-relaxed text-grey-700 max-w-2xl mx-auto"
            >
              Great things are better shared. When you refer a neighbor or friend to Hudson My Home&#8482; and they become a member, you both get a free month of membership. Whether it&apos;s a holiday gift, a new neighbor, or your parents&apos; home &mdash; everyone deserves a Hudson.
            </motion.p>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-20 lg:py-28 border-t border-black/[0.06]">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
            <FadeIn className="text-center mb-16 lg:mb-20">
              <h2
                className="font-serif font-normal text-grey-950"
                style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.12, letterSpacing: '-0.02em' }}
              >
                How it works
              </h2>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
              {STEPS.map((step, i) => (
                <FadeIn key={step.num} delay={i * 0.1}>
                  <div className="text-center md:text-left h-full flex flex-col items-center md:items-start">
                    <span
                      className="font-serif text-4xl lg:text-5xl tabular-nums leading-none mb-5"
                      style={{ color: colors.teal }}
                    >
                      {step.num}
                    </span>
                    <h3
                      className="font-serif text-xl lg:text-2xl text-grey-950 mb-4"
                      style={{ letterSpacing: '-0.02em' }}
                    >
                      {step.title}
                    </h3>
                    <p className="font-sans text-base sm:text-base leading-relaxed text-grey-600 flex-1">{step.body}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28" style={{ backgroundColor: colors.tealDark }}>
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <FadeIn>
              <h2
                className="font-serif font-normal text-white"
                style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.12, letterSpacing: '-0.02em' }}
              >
                Know Someone Who Deserves a Hudson?
              </h2>
              <p className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-white/80 max-w-2xl mx-auto">
                Referral tracking is handled through our partner system. Reach out to your Hudson representative or
                contact us to get your referral link.
              </p>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, ease, delay: 0.15 }}
                className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <a
                  href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 font-sans font-semibold text-base text-white transition-opacity hover:opacity-90 rounded-none"
                  style={{ backgroundColor: colors.copper }}
                >
                  Book Your Consultation
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </a>
                <a
                  href="mailto:info@hudsonmyhome.com"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 font-sans font-semibold text-base border-2 border-white/40 text-white transition-opacity hover:opacity-90 rounded-none"
                >
                  Contact Us to Get Your Link
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-8 font-sans text-sm text-white/60"
              >
                Questions? Call us at{' '}
                <a href="tel:+15152164433" className="text-white/90 hover:text-white underline-offset-4 hover:underline">
                  (515) 216-4433
                </a>
              </motion.p>
            </FadeIn>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default ReferPage
